import { arrayMove } from '@dnd-kit/sortable';
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	Stack,
	Typography,
} from '@mui/material';
import { closeSnackbar, useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DndArticleListSection from './single-info-order/DndArticleListSection';
import CloseIcon from '@mui/icons-material/Close';
import FormatLineSpacingIcon from '@mui/icons-material/FormatLineSpacing';
import { useParams } from 'react-router-dom';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
	useGetCrossoverSingleSortListQuery,
	useSetCrossoverSingleSortMutation,
} from '@/api/exercise/crossoverApiSlice';

interface Props {}

export default function SetSingleOrderDialog(props: Props) {
	const { t } = useTranslation();
	const { enqueueSnackbar } = useSnackbar();
	const [open, setOpen] = useState(false);
	const [setCrossoverSingleSort] = useSetCrossoverSingleSortMutation();
	const { crossoverId } = useParams();

	const [meditationItems, setMeditationItems] = useState<any>([]);

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetCrossoverSingleSortListQuery(crossoverId || '', {
			refetchOnMountOrArgChange: true,
			skip: !open,
		});

	useEffect(() => {
		const list = Array.isArray(propertySummary?.data)
			? propertySummary?.data
			: [];
		setMeditationItems(
			(list || []).map((item) => ({
				...item,
				singleId: item.singleId,
				id: item.singleId,
			}))
		);
	}, [isGetPropertySummarySuccess, propertySummary]);

	const handleRepositionItem = (activeIndex: number, overIndex: number) => {
		setMeditationItems((prev: unknown[]) =>
			arrayMove(prev, activeIndex, overIndex)
		);
	};

	const onSubmit = async () => {
		const response = await setCrossoverSingleSort({
			singleIdList: meditationItems.map(
				(item: { singleId: any }) => item.singleId
			),
			id: crossoverId || '',
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.sortSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			handleClose();
		}
	};

	const handleClose = () => {
		setOpen(false);
	};

	const formContent = (
		<>
			<Button
				variant='contained'
				style={{ alignItems: 'center' }}
				endIcon={<FormatLineSpacingIcon />}
				onClick={() => setOpen(true)}
			>
				{t('meditation.rearrangement')}
			</Button>
			<Dialog
				open={open}
				onClose={() => handleClose()}
				aria-labelledby='alert-dialog-title'
				aria-describedby='alert-dialog-description'
				maxWidth='xl'
				fullWidth
			>
				<DialogTitle variant='h5'>{t('meditation.rearrangement')}</DialogTitle>
				<IconButton
					aria-label='close'
					onClick={() => handleClose()}
					sx={{
						position: 'absolute',
						right: 8,
						top: 8,
						color: (theme) => theme.palette.grey[500],
					}}
				>
					<CloseIcon />
				</IconButton>
				<DialogContent>
					<Grid container alignItems='stretch' columnSpacing={3} marginTop={3}>
						<Grid item xs display='flex'>
							<Stack>
								{meditationItems && (
									<DndArticleListSection
										meditationItems={meditationItems}
										handleRepositionItem={handleRepositionItem}
									/>
								)}
							</Stack>
						</Grid>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => handleClose()}>{t('button.cancel')}</Button>

					<Button type='submit' variant='contained' onClick={() => onSubmit()}>
						{t('button.save')}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
	return <>{formContent}</>;
}
